import { useMemo } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldTitle,
} from "@volca/ui/components/ui/field";
import { MultiSelect } from "@volca/ui/components/ui/multi-select";
import {
  RadioGroup,
  RadioGroupItem,
} from "@volca/ui/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import { Stack } from "@volca/ui/components/ui/stack";
import { TimeRangePicker } from "@volca/ui/components/ui/time-range-picker";
import { useApBusinessTimeZone } from "@/accounts-payable/common";
import { assigneeFilterOptions } from "@/accounts-payable/common/owner-and-action-filters/utils";
import { useGetAllVendors, useGetApAssignees } from "@/accounts-payable/query";
import { apToday } from "@/accounts-payable/utils";
import useAuth from "@/core/auth/use-auth";
import { dayjs } from "@/core/utils/date";
import {
  EXPORT_DATE_FIELDS,
  type ExportDateField,
  type InvoiceExportOptions,
  exportPresets,
} from "./export-options";
import type { InvoiceExportKind } from "./invoice-export";
import type { InvoiceFilter } from "./use-invoices-tab";

const DAY = "YYYY-MM-DD";

const FORMATS: { kind: InvoiceExportKind; title: string; hint: string }[] = [
  {
    kind: "invoices",
    title: "One row per invoice",
    hint: "Totals, status, job and review",
  },
  {
    kind: "line_items",
    title: "One row per line item",
    hint: "What each invoice bought, against the pricebook",
  },
];

export const ExportFields = ({
  businessId,
  statusFilters,
  options,
  onChange,
  disabled,
}: {
  businessId: string | null;
  statusFilters: InvoiceFilter[];
  options: InvoiceExportOptions;
  onChange: (changes: Partial<InvoiceExportOptions>) => void;
  disabled: boolean;
}) => {
  const { user } = useAuth();
  const today = apToday(useApBusinessTimeZone());
  const presets = useMemo(() => exportPresets(today), [today]);
  const vendors = useGetAllVendors(businessId);
  const people = useGetApAssignees(businessId);
  const { from, to } = options.days ?? {};
  // The picker works in local-midnight Dates, as the list's Day filter does.
  const range = useMemo(
    () =>
      from && to
        ? {
            from: dayjs(from, DAY).toDate(),
            to: dayjs(to, DAY).toDate(),
            label: "",
          }
        : null,
    [from, to],
  );
  const carried = [
    options.search && `search “${options.search}”`,
    options.actionTypes.length > 0 && "Action filter",
  ].filter(Boolean);

  return (
    <FieldGroup>
      <RadioGroup
        aria-label="Format"
        value={options.kind}
        disabled={disabled}
        onValueChange={(kind) => onChange({ kind: kind as InvoiceExportKind })}
      >
        {FORMATS.map(({ kind, title, hint }) => (
          <FieldLabel key={kind} htmlFor={`export-format-${kind}`}>
            <Field orientation="horizontal">
              <RadioGroupItem value={kind} id={`export-format-${kind}`} />
              <FieldContent>
                <FieldTitle>{title}</FieldTitle>
                <FieldDescription>{hint}</FieldDescription>
              </FieldContent>
            </Field>
          </FieldLabel>
        ))}
      </RadioGroup>

      <Stack direction="row" gap={2} align="end" className="w-full">
        <Field className="w-36 flex-none">
          <FieldLabel htmlFor="export-date-field">Date</FieldLabel>
          <Select
            value={options.dateField}
            items={EXPORT_DATE_FIELDS}
            disabled={disabled}
            onValueChange={(field) =>
              onChange({ dateField: field as ExportDateField })
            }
          >
            <SelectTrigger id="export-date-field">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {Object.entries(EXPORT_DATE_FIELDS).map(([field, label]) => (
                  <SelectItem key={field} value={field}>
                    {label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
        {/* The picker has no disabled state of its own; a disabled fieldset
            locks its trigger while an export runs, like the other fields. */}
        <Stack
          render={<fieldset disabled={disabled} />}
          gap={0}
          align="stretch"
          className="min-w-0 flex-1"
        >
          <TimeRangePicker
            label="Range"
            placeholder="All dates"
            presets={presets}
            value={range}
            fullWidth
            onChange={(next) =>
              onChange({
                days: next && {
                  from: dayjs(next.from).format(DAY),
                  to: dayjs(next.to).format(DAY),
                },
              })
            }
          />
        </Stack>
      </Stack>

      <MultiSelect
        label="Vendors"
        placeholder="All vendors"
        loading={vendors.isPending}
        disabled={disabled}
        options={[
          ...(vendors.data ?? []).map(({ id, name }) => ({ id, name })),
          ...(vendors.isError
            ? [{ id: "error", name: "Couldn't load vendors", disabled: true }]
            : []),
        ]}
        value={options.vendorIds}
        onChange={(vendorIds) => onChange({ vendorIds })}
      />

      <Field>
        <FieldLabel htmlFor="export-status">Status</FieldLabel>
        <Select
          value={options.status}
          items={Object.fromEntries(
            statusFilters.map(({ key, label }) => [key, label]),
          )}
          disabled={disabled}
          onValueChange={(status) => onChange({ status: status as string })}
        >
          <SelectTrigger id="export-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {statusFilters.map(({ key, label }) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </Field>

      <MultiSelect
        label="Assignee"
        placeholder="Anyone"
        loading={people.isPending}
        disabled={disabled}
        options={assigneeFilterOptions(people.data ?? [], user?.id)}
        value={options.assigneeTokens}
        onChange={(assigneeTokens) => onChange({ assigneeTokens })}
      />

      {carried.length > 0 && (
        <Stack direction="row" gap={1} align="center" wrap>
          <FieldDescription>
            Also limited to the list’s {carried.join(" and ")}.
          </FieldDescription>
          <Button
            variant="link"
            size="sm"
            className="h-auto px-0"
            disabled={disabled}
            onClick={() => onChange({ search: "", actionTypes: [] })}
          >
            Don’t apply
          </Button>
        </Stack>
      )}
    </FieldGroup>
  );
};
